import { Fragment, useEffect } from 'react';
import { Button, Typography } from '@material-tailwind/react';
import { CardItemProps, Cards } from '../../../components/card/cards';
import QuickMenuGrid from '../../../components/home/QuickMenuGrid';
import FloatingBar from '../../../components/home/floating-bar/FloatingBar';

import '../../../styles/home/home.css';
import { useNavigate } from 'react-router-dom';
import { ProductSubType } from '../../../enum';
import CourseList from '../../../components/course/CourseList';
import MainBanner from '../../../components/carousel/MainBanner';
import Popup from '../../../components/modal/Popup';
import { PopupCategory } from '../../../constants/types/popup-type';
import {
  StoryCourse,
  StoryMethod,
  StoryMission,
  StoryReview,
  StoryTimeLine,
} from '../../../components/home/story';
import { useSiteConfig } from '../../../hooks/useSiteConfig';
import { SITE_CONFIG_KEY } from '../../../constants/types/site-config-type';
import { layout } from '../../../constants/urlconfig';
import { COURSE_CATEGORIES } from '../../../constants/course-categories';

const SITE_CONFIG_KEYS = [
  SITE_CONFIG_KEY.JEBS_REVIEW_BLOG_MORE,
  SITE_CONFIG_KEY.JEBS_REVIEW_VIDEO_MORE,
];

const HOME_REVIEW_DATA: CardItemProps[] = [
  {
    img: 'images/home/review/review1.png',
    title: '수요일에도 교회에 모여 영어로 말씀을 묵상해요!',
    link: '/review/details/2',
    type: 'internal',
  },
  {
    img: 'images/home/review/review2.png',
    title: '하나님과 동행하는 지도자 되기 프로젝트는 계속 될 것입니다. ',
    link: '/review/details/1',
    type: 'internal',
  },
  {
    img: 'images/home/review/review3.png',
    title: '엄마 손 잡고 첫 수업하던 아이의 확실한 비포&애프터',
    link: '/review/details/8',
    type: 'internal',
  },
  {
    img: 'images/home/review/review4.png',
    title: '기도의 삼박자로 은혜가 배가 되는 젭스영어교실!',
    link: '/review/details/6',
    type: 'internal',
  },
];

// 메인 하단 코스 목록 섹션 - 경로/제목은 COURSE_CATEGORIES 공용
const HOME_COURSE_SECTIONS = [
  {
    productSubType: ProductSubType.HOMESCHOOL,
    heading: '클래스를 개설하여 자녀/학생들과 수업을 시작하세요.',
  },
  {
    productSubType: ProductSubType.WORSHIP,
    heading: '우리 교회에 영어주일학교를 도입하세요.',
  },
].map((section) => ({
  ...section,
  ...COURSE_CATEGORIES.find(
    (category) => category.productSubType === section.productSubType,
  ),
}));

const Home = () => {
  const navigate = useNavigate();
  const { configs } = useSiteConfig(SITE_CONFIG_KEYS);

  // 후기 더보기 링크 (설정이 없으면 젭스 공식 블로그/유튜브로 이동)
  const blogMoreUrl =
    configs[SITE_CONFIG_KEY.JEBS_REVIEW_BLOG_MORE] || layout.blog;
  const videoMoreUrl =
    configs[SITE_CONFIG_KEY.JEBS_REVIEW_VIDEO_MORE] || layout.youtube;

  const isStudent = localStorage.getItem('type') === 'STUDENT' ? true : false;

  useEffect(() => {
    if (isStudent) {
      navigate('/student');
    }
  }, []);

  return (
    <Fragment>
      <Popup categoryId={PopupCategory.MAIN} />

      {/* 메인 배너 영역 */}
      <section className="m-auto w-full max-w-[1920px]">
        <MainBanner />
      </section>

      {/* 바로가기 영역 */}
      <section className="m-auto mt-4 grid max-w-[1440px] px-5 lg:px-14">
        <Typography variant="h4" className="my-4 text-xl md:text-2xl">
          젭스에 오신 걸 환영합니다!
        </Typography>

        {/* 젭스 바로가기 메뉴 그리드 */}
        <QuickMenuGrid />
      </section>

      {/* 젭스 스토리 영역 */}
      <section className="m-auto mt-4 grid max-w-[1440px] px-5 lg:px-14">
        <Typography variant="h4" className="my-4 text-xl md:text-2xl">
          젭스 Story
        </Typography>

        <StoryMission />
        <StoryTimeLine />
        <StoryCourse />
        <StoryMethod />
        <StoryReview />
      </section>

      {/* 회원 후기 목록 영역 */}
      <section className="m-auto mt-10 grid max-w-[1440px] px-5 md:mt-20 lg:px-14">
        <Typography variant="h4" className="text-xl md:text-2xl">
          성경적 영어교육은 이렇게 다릅니다!
        </Typography>
        <Typography variant="h6" className="font-normal">
          실제 젭스를 하고 계신 분들의 생생한 후기
        </Typography>
        <Cards items={HOME_REVIEW_DATA} />
        <div className="mt-6 inline-grid grid-cols-1 gap-4 justify-self-center md:grid-cols-2">
          <Button
            variant="text"
            className="border-purple-00 block border"
            onClick={() =>
              window.open(blogMoreUrl, '_blank', 'noopener,noreferrer')
            }
          >
            <Typography
              variant="h6"
              className="text-purple-05 text-center font-semibold"
            >
              블로그 후기 더보기
            </Typography>
          </Button>

          <Button
            variant="text"
            className="border-purple-00 block border"
            onClick={() =>
              window.open(videoMoreUrl, '_blank', 'noopener,noreferrer')
            }
          >
            <Typography
              variant="h6"
              className="text-purple-05 text-center font-semibold"
            >
              영상 후기 더보기
            </Typography>
          </Button>
        </div>
      </section>

      {/* 코스 목록 영역 - 영어교실 / 주일학교 */}
      {HOME_COURSE_SECTIONS.map(
        ({ heading, productSubType, to, pageTitle }) => (
          <section
            key={productSubType}
            className="m-auto mt-[80px] max-w-[1440px] px-5 lg:px-14"
          >
            <Typography variant="h4" className="text-xl md:text-2xl">
              {heading}
            </Typography>
            <div className="mt-6">
              <CourseList productSubType={productSubType} max={4} />
            </div>
            <Button
              variant="text"
              className="border-purple-00 mx-auto mt-6 block border"
              onClick={() => navigate(to)}
            >
              <Typography
                variant="h6"
                className="text-purple-05 text-center font-semibold"
              >
                {pageTitle} 전체보기
              </Typography>
            </Button>
          </section>
        ),
      )}

      <FloatingBar />
    </Fragment>
  );
};

export default Home;
